import type {
  BootstrapRoute,
  CloudpackConfig,
  ExpandedSourcePath,
  PackageJson,
  RenderedRoute,
  Route,
  ShorthandRenderedRoute,
  ShorthandRoute,
} from '@ms-cloudpack/common-types';
import { isShorthandBootstrapRoute, isShorthandRenderedRoute } from '@ms-cloudpack/common-types';
import { readAppConfig, type ReadAppConfigOptions } from './readAppConfig.js';
import { readGeneratedConfig } from './readGeneratedConfig.js';
import { intermediateToSourcePath, sourceToIntermediatePath } from '@ms-cloudpack/path-utilities';
import { flattenExportsMap } from '@ms-cloudpack/package-utilities';
import path from 'path';
import { cloneJson, readJsonSync } from '@ms-cloudpack/json-utilities';
import { normalizeRelativePath } from '@ms-cloudpack/path-string-parsing';

/**
 * package.json with only the keys used in this file, since we're missing some logic from
 * PackageDefinitions which is assumed in the full type.
 */
type PartialPackageJson = Pick<PackageJson, 'exports' | 'module' | 'main'>;

export type ReadConfigOptions = ReadAppConfigOptions & Partial<Pick<CloudpackConfig, 'mode' | 'logBundleInfo'>>;

/**
 * Read the app config and generated config if they exist.
 *
 * Throws if there's an error reading a file or certain settings are invalid. (As of writing,
 * this doesn't do full schema validation; it only validates certain specific settings.)
 */
export async function readConfig(appPath: string, options?: ReadConfigOptions): Promise<CloudpackConfig> {
  const { mode = 'library', logBundleInfo, configFileName, ...appConfigOptions } = options || {};

  const {
    appConfig: { routes, ...appConfig },
    appConfigPaths,
  } = await readAppConfig(appPath, { ...appConfigOptions, configFileName });
  const generatedConfig = await readGeneratedConfig(appPath);

  const { getConfigPath } = await import('../getConfigPath.js');
  const { generatedConfigPath } = getConfigPath(appPath);

  const config: CloudpackConfig = {
    ...appConfig,
    appPath,
    mode,
    generated: generatedConfig,
    generatedSnapshot: cloneJson(generatedConfig),
    configPaths: {
      generatedConfigPath,
      appConfigPaths,
    },
  };

  if (logBundleInfo) {
    // Only set this if true, mainly to avoid interfering with tests.
    config.logBundleInfo = true;
  }

  // Expands all routes which refer to source files into fully transformed route objects.
  if (routes?.length) {
    config.routes = expandRoutes(routes, appPath);
  }

  return config;
}

/**
 * Ensure all source entries in routes are expanded to fully qualified entry paths.
 */
function expandRoutes(routes: ShorthandRoute[], appPath: string): Route[] {
  // Cached flattened exports for appPath, used to expand entries
  const flattenedExports = getFlattenedExports(appPath);

  return routes.map((route) => {
    if (!isShorthandRenderedRoute(route) && !isShorthandBootstrapRoute(route)) {
      // Skip extra processing for other route types.
      return route;
    }

    // eslint-disable-next-line etc/no-deprecated
    const { entry, exportEntry, serverEntry, ...rest } = route as ShorthandRenderedRoute;
    const renderedRoute: RenderedRoute | BootstrapRoute = rest;

    if (entry?.length) {
      // Expand the entry to full info about the file path.
      renderedRoute.entry = expandEntry({ entry, appPath, flattenedExports });
    }

    if (serverEntry) {
      // eslint-disable-next-line etc/no-deprecated
      if (renderedRoute.renderScript) {
        throw new Error(
          // eslint-disable-next-line etc/no-deprecated
          `A route cannot have both serverEntry ("${serverEntry}") and renderScript ("${renderedRoute.renderScript}").`,
        );
      }

      renderedRoute.serverEntry = expandRouteEntry({
        // Usually entryPath will be a source path, but also handle if it's an exports map key.
        importPath: flattenedExports[serverEntry] ? serverEntry : undefined,
        filePath: flattenedExports[serverEntry] || serverEntry,
        entryType: 'serverEntry',
        appPath,
      });
    }

    if (exportEntry) {
      // Handle backwards compatibility with moving from exportEntry to entry.
      const filePath = flattenedExports[exportEntry];

      if (!filePath) {
        throw new Error(
          `A route referenced exportEntry "${exportEntry}" but this couldn't be resolved from the package.json.`,
        );
      }

      const expanded = expandRouteEntry({ importPath: exportEntry, filePath, appPath });

      console.warn(
        'The route property "exportEntry" is deprecated. Update your route as follows:',
        JSON.stringify({ ...renderedRoute, entry: expanded.sourcePath }, null, 2),
      );
      (renderedRoute.entry ??= []).push(expanded);
    }

    return renderedRoute;
  });
}

function expandEntry(params: {
  entry: ShorthandRenderedRoute['entry'];
  appPath: string;
  flattenedExports: Record<string, string | undefined>;
}): ExpandedSourcePath[] {
  const { entry, appPath, flattenedExports } = params;
  const entryArray = Array.isArray(entry) ? entry : entry !== undefined ? [entry] : [];

  return entryArray.map((entryPath) =>
    expandRouteEntry({
      // Usually entryPath will be a source path, but also handle if it's an exports map key.
      importPath: flattenedExports[entryPath] ? entryPath : undefined,
      filePath: flattenedExports[entryPath] || entryPath,
      appPath,
    }),
  );
}

/**
 * Get flattened exports from package.json at `appPath`.
 * If there's no export for `.`, it will be filled in from `module || main`.
 */
function getFlattenedExports(appPath: string): Record<string, string | undefined> {
  // We can't use PackageDefinitions.get here because we're reading the config, and definitions
  // cache requires config resulting in a cycle.
  const definition = readJsonSync<PartialPackageJson>(path.join(appPath, 'package.json')) || {};
  const flatExports: Record<string, string | undefined> = flattenExportsMap(definition.exports || {});
  flatExports['.'] ??= definition.module || definition.main;
  return flatExports;
}

/** Expands shorthand route entries into fully qualified ones. */
function expandRouteEntry(options: {
  importPath: string | undefined;
  filePath: string;
  appPath: string;
  entryType?: 'entry' | 'serverEntry';
}): ExpandedSourcePath {
  const { filePath, appPath, importPath, entryType = 'entry' } = options;
  const sourcePath = intermediateToSourcePath(filePath, appPath);

  if (!sourcePath) {
    throw new Error(`Could not resolve source path for ${entryType}: ${filePath}`);
  }

  return {
    // Used to derive bundle entry path
    sourcePath,
    // Import map key
    importPath: importPath ? normalizeRelativePath(importPath) : sourceToIntermediatePath(sourcePath),
  };
}
